<template lang="pug">
  .b-spec-chat-drawer(data-testid="spec-chat-drawer")
    ._head
      ._title Чат по позиции
      button._close(type="button" data-testid="spec-chat-drawer-close" @click="$emit('close')") ×
    ._item(data-testid="spec-chat-drawer-item") {{ item && item.name }}

    ._empty(v-if="!chats.length") Чатов по этой позиции пока нет
    ._tabs(v-else-if="chats.length > 1")
      button._tab(
        v-for="chat in chats"
        :key="chat.id"
        type="button"
        data-testid="spec-chat-drawer-tab"
        :class="{ active: activeChat && activeChat.id === chat.id }"
        @click="select(chat)"
        ) {{ chat.title }}

    template(v-if="activeChat")
      ._chat-title(v-if="chats.length === 1") {{ activeChat.title }}
      ._feed(ref="feed" data-testid="spec-chat-drawer-feed")
        ._empty(v-if="!messages.length") Сообщений пока нет
        template(v-for="message in messages")
          ._event(v-if="message.authorType === 'system'" :key="message.id" data-testid="spec-chat-drawer-event")
            span._time {{ dateTime(message.createdAt) }}
            span._event-text {{ eventText(message) }}
            span._event-items
              template(v-for="(eventItem, i) in eventItems(message)")
                button._link(
                  v-if="message.eventType !== 'items_deleted'"
                  :key="`l-${i}`"
                  type="button"
                  @click="$emit('goto-item', eventItem)"
                  ) {{ eventItem.name }}
                span(v-else :key="`t-${i}`") {{ eventItem.name }}
              span(v-if="eventRestCount(message)") и ещё {{ eventRestCount(message) }}
            button._link(
              v-if="message.eventData && message.eventData.request"
              type="button"
              @click="$emit('goto-request')"
              ) запрос КП
            a._link(
              v-for="file in message.files"
              :key="`f-${file.id}`"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
              ) {{ file.name }}
          ._message(v-else :key="message.id" data-testid="spec-chat-drawer-message")
            span._author {{ message.authorName || authorFallback(message) }}
            span._time {{ dateTime(message.createdAt) }}
            span._text(data-testid="spec-chat-drawer-message-text") {{ message.text }}
            a._link(
              v-for="file in message.files"
              :key="`mf-${file.id}`"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
              ) {{ file.name }}

      ._form(v-if="!readOnly")
        textarea._input(data-testid="spec-chat-drawer-input" v-model="text" placeholder="Сообщение")
        //- кнопки слева: правый нижний угол занимает виджет поддержки сайта
        ._form-row
          Btn(blue sm data-testid="spec-chat-drawer-send" :disabled="!canSend" @click="send") Отправить
          label._attach
            | Прикрепить
            input._file(type="file" multiple data-testid="spec-chat-drawer-attach" @change="onFiles")
          span._attached(v-if="files.length") файлов: {{ files.length }}
</template>

<script>
import specChat from '@/model/specChat';
import { apiRun } from '@/model/apiRun';
import { dateTime } from '@/utils/spec';

/**
 * Шторка «Чат по позиции» на экране спецификации (ТЗ 6.2): открывается из меню строки и не уводит
 * со страницы. Собеседники — заказчик и поставщики, в чьи запросы КП входит позиция; если их
 * несколько, переключаются вкладками, сразу открыт чат с самым свежим сообщением. Лента и отправка
 * — те же операции, что на вкладке «Чаты» (ТЗ 6.10); сама вкладка остаётся как есть.
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecChatDrawer',
  props: {
    item: Object,
    /** Чаты позиции в порядке списка чатов — по дате последнего сообщения. */
    chats: { type: Array, default: () => [] },
    readOnly: Boolean,
  },
  data() {
    return {
      activeChat: null,
      messages: [],
      text: '',
      files: [],
    };
  },
  computed: {
    canSend() {
      return this.text.trim().length > 0 || this.files.length > 0;
    },
  },
  watch: {
    chats: {
      immediate: true,
      handler(chats) {
        if (chats.length && !chats.some((c) => c.id === this.activeChat?.id)) this.select(chats[0]);
      },
    },
  },
  methods: {
    dateTime,
    eventText: specChat.eventText,
    eventItems: specChat.eventItems,
    eventRestCount: specChat.eventRestCount,
    async select(chat) {
      this.activeChat = chat;
      this.messages = [];
      await this.loadMessages();
    },
    async loadMessages() {
      const chatId = this.activeChat?.id;
      if (!chatId) return;
      const messages = await specChat.fetchMessages(chatId);
      // пока грузилось, могли переключить вкладку
      if (this.activeChat?.id !== chatId) return;
      this.messages = messages;
      this.$nextTick(() => {
        const { feed } = this.$refs;
        if (feed) feed.scrollTop = feed.scrollHeight;
      });
    },
    authorFallback(message) {
      const map = { designer: 'Дизайнер', supplier: 'Поставщик', customer: 'Заказчик' };
      return map[message.authorType] ?? '';
    },
    onFiles(event) {
      this.files = Array.from(event.target.files ?? []);
    },
    async send() {
      if (!this.canSend || !this.activeChat) return;
      const sent = await apiRun(
        () => specChat.sendMessage(this.activeChat.id, this.text.trim(), this.files),
        'Не удалось отправить сообщение',
      );
      if (!sent) return;
      this.text = '';
      this.files = [];
      await this.loadMessages();
    },
  },
};
</script>

<style scoped lang="scss">
.spec-chat-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 560px;
  max-width: 100%;
  z-index: 9000;
  background: var(--color-white);
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.14);
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__head {
    display: flex;
    align-items: center;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    flex-grow: 1;
  }

  &__close {
    border: none;
    background: none;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }

  &__item,
  &__chat-title {
    font-size: 13px;
    color: var(--color-middle-gray);
  }

  &__chat-title {
    font-weight: 700;
  }

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__tab {
    padding: 6px 12px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    background: var(--color-white);
    font: inherit;
    font-size: 13px;
    cursor: pointer;

    &.active {
      border-color: var(--color-accent);
      color: var(--color-accent);
    }
  }

  &__feed {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
  }

  &__message,
  &__event {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 13px;
  }

  &__event {
    padding: 8px 12px;
    border-radius: 8px;
    background: #F1F5FA;
    color: var(--color-middle-gray);
  }

  &__event-text,
  &__text {
    flex-basis: 100%;
    color: var(--color-dark-gray);
  }

  &__text {
    white-space: pre-wrap;
  }

  &__event-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex-basis: 100%;
  }

  &__link {
    border: none;
    background: none;
    font: inherit;
    padding: 0;
    color: var(--color-accent);
    cursor: pointer;
  }

  &__author {
    font-weight: 700;
    color: var(--color-dark-gray);
  }

  &__time {
    color: var(--color-middle-gray);
    font-size: 11px;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__input {
    min-height: 64px;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    font: inherit;
    font-size: 13px;
    resize: vertical;
  }

  &__form-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__attach {
    color: var(--color-accent);
    cursor: pointer;
    font-size: 13px;
  }

  &__file {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    visibility: hidden;
  }

  &__attached {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__empty {
    color: var(--color-middle-gray);
    font-size: 13px;
  }
}
</style>
